import { RoomEntity, StreamId } from "@common/domain";
import { createFeatureSelector, createSelector } from "@ngrx/store";
import {
  RoomStreamVO,
  ROOM_STAGE_PARTICIPANTS_CAPACITY
} from "@youm-front/room/domain";
import { RoomState, ROOM_FEATURE_KEY } from "../reducers";
import {
  roomLocalActiveStreams,
  roomLocalStreams
} from "./room-local-streams.selectors";
import {
  roomRemoteActiveStreams,
  roomRemoteStreams
} from "./room-remote-streams.selectors";
import * as RoomStageLayoutSelectors from "./room-stage-layout.selectors";

const roomState = createFeatureSelector<RoomState>(ROOM_FEATURE_KEY);

export const room = createSelector(roomState, (state) =>
  state.room ? new RoomEntity(state.room) : state.room
);
export const joinInProgress = createSelector(
  roomState,
  (state) => state.joinInProgress
);
export const joinSucceed = createSelector(
  roomState,
  (state) => state.joinSucceed
);
export const isLoading = createSelector(roomState, (state) => state.isLoading);
export const roomStreams = createSelector(
  roomLocalStreams,
  roomRemoteStreams,
  (roomLocalStreams, roomRemoteStreams) => [
    ...roomLocalStreams,
    ...roomRemoteStreams
  ]
);
export const roomStreamsMap = createSelector(
  roomStreams,
  (roomStreams: RoomStreamVO[]) => {
    return roomStreams.reduce((acc, stream: RoomStreamVO) => {
      return acc.set(stream.value.streamId, stream);
    }, new Map<StreamId, RoomStreamVO>());
  }
);
export const roomActiveStreams = createSelector(
  roomLocalActiveStreams,
  roomRemoteActiveStreams,
  (roomLocalActiveStreams, roomRemoteActiveStreams) => [
    ...roomLocalActiveStreams,
    ...roomRemoteActiveStreams
  ]
);
export const roomStageCapacityExhausted = createSelector(
  roomActiveStreams,
  (roomActiveStreams) =>
    roomActiveStreams.length >= ROOM_STAGE_PARTICIPANTS_CAPACITY
);
export const videos = createSelector(
  RoomStageLayoutSelectors.videos,
  roomStreamsMap,
  (videos, streamsMap) => {
    return videos
      .map((_) => streamsMap.get(_))
      .filter((_): _ is RoomStreamVO => !!_);
  }
);
export const videosMap = createSelector(
  RoomStageLayoutSelectors.videos,
  roomStreamsMap,
  (videos, streamsMap) => {
    // Make sure staged elements are the first ones in a Map and
    // in a right order.
    return new Map([
      ...videos
        .map((_) => streamsMap.get(_))
        .filter((_): _ is RoomStreamVO => !!_)
        .map((_) => [_.value.streamId, _] as const),
      ...streamsMap
    ]);
  }
);
export const audios = createSelector(
  RoomStageLayoutSelectors.audios,
  roomStreamsMap,
  (audios, streamsMap) => {
    return audios
      .map((_) => streamsMap.get(_))
      .filter((_): _ is RoomStreamVO => !!_);
  }
);
